import { createContext, useContext, useState, useEffect } from 'react';
import { me as apiMe, login as apiLogin, logout as apiLogout } from './services';

const AuthContext = createContext();

const isCanceledError = (error) =>
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message === 'canceled';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bootstrapped, setBootstrapped] = useState(false);
    const [firstLoginToken, setFirstLoginToken] = useState(null);

    const fetchAndSetUser = async (signal, { withLoading = false } = {}) => {
        if (withLoading) setLoading(true);
        let canceled = false;
        try {
            const userData = await apiMe(signal);
            setUser(userData);
            return userData;
        } catch (e) {
            if (isCanceledError(e)) {
                canceled = true;
                return null;
            }
            setUser(null);
            return null;
        } finally {
            if (!canceled) {
                setBootstrapped(true);
            }
            if (withLoading) setLoading(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        fetchAndSetUser(controller.signal, { withLoading: true });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const handleUnauthorized = () => {
            setUser(null);
            setFirstLoginToken(null);
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    }, []);

    const login = async (email, password) => {
        setFirstLoginToken(null);
        try {
            const res = await apiLogin(email, password);

            if (res?.requires_password_reset) {
                setUser(null);
                setFirstLoginToken(res?.token || null);
                return {
                    success: false,
                    requiresPasswordReset: true,
                    token: res?.token || null,
                };
            }

            const userData = await fetchAndSetUser(undefined, { withLoading: true });
            setFirstLoginToken(null);
            return {
                success: !!userData,
                requiresPasswordReset: false,
            };
        } catch (error) {
            console.error('Login error:', error.message || error);
            setUser(null);
            setFirstLoginToken(null);
            return { success: false, error: error?.message || 'Login failed' };
        }
    };

    const logout = async () => {
        try {
            await apiLogout();
            setUser(null);
        } catch (error) {
            console.error('Logout error:', error.message || error);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                login,
                logout,
                loading,
                bootstrapped,
                refreshUser: fetchAndSetUser,
                firstLoginToken,
                setFirstLoginToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
